import type { StorefrontAdapter, StorefrontCartItem, StorefrontOrder, StorefrontProduct } from '../types';
import { loadScriptOnce } from '../types';
import { trackingConsentGranted } from '../consent';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function money(n: unknown): number {
  const v = Number(n ?? 0);
  return Number.isFinite(v) ? Math.round(v * 100) / 100 : 0;
}

function gaItem(i: StorefrontCartItem) {
  return {
    item_id: i.sku ?? i.variant_id ?? i.product_id,
    item_name: i.product_name,
    item_variant: i.variant_name ?? undefined,
    price: money(i.price),
    quantity: i.quantity,
  };
}

function ensureGtag(): (...args: unknown[]) => void {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    // Standard snippet: gtag() pushes `arguments` (not an array) onto the dataLayer.
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  }
  return window.gtag;
}

function allowed(cfg: Record<string, string>): boolean {
  return cfg.require_consent !== '1' || trackingConsentGranted();
}

/** Last purchase reported, so a reloaded confirmation page does not double count. */
function purchaseAlreadySent(orderId: string): boolean {
  try {
    const key = `rumio_ga_purchase_${orderId}`;
    if (sessionStorage.getItem(key)) return true;
    sessionStorage.setItem(key, '1');
  } catch {
    /* storage blocked: fire anyway */
  }
  return false;
}

export const googleTagsAdapter: StorefrontAdapter = {
  id: 'google_tags',
  isConfigured: (cfg) => !!(cfg.measurement_id || cfg.gtm_id),
  load(cfg) {
    liveConfig = cfg;
    if (!allowed(cfg)) return;
    const gtag = ensureGtag();
    if (cfg.gtm_id) {
      window.dataLayer!.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
      loadScriptOnce('gtm-container', `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(cfg.gtm_id)}`);
    }
    const first = cfg.measurement_id || cfg.ads_id;
    if (first) {
      loadScriptOnce('gtag-js', `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(first)}`);
      gtag('js', new Date());
      if (cfg.measurement_id) gtag('config', cfg.measurement_id, { send_page_view: true });
      if (cfg.ads_id) gtag('config', cfg.ads_id, { allow_enhanced_conversions: cfg.user_provided_data === '1' });
    }
  },
  viewedProduct(p: StorefrontProduct) {
    const cfg = current();
    if (!cfg || cfg.ecommerce_events === '0' || !allowed(cfg)) return;
    ensureGtag()('event', 'view_item', {
      currency: cfg.currency || 'USD',
      value: money(p.price),
      items: [{ item_id: p.sku ?? p.id, item_name: p.name, price: money(p.price), quantity: 1, item_category: p.categories?.[0] }],
    });
  },
  addedToCart(item) {
    const cfg = current();
    if (!cfg || cfg.ecommerce_events === '0' || !allowed(cfg)) return;
    ensureGtag()('event', 'add_to_cart', { currency: cfg.currency || 'USD', value: money(item.price * item.quantity), items: [gaItem(item)] });
  },
  startedCheckout(cart, total) {
    const cfg = current();
    if (!cfg || cfg.ecommerce_events === '0' || !allowed(cfg)) return;
    ensureGtag()('event', 'begin_checkout', { currency: cfg.currency || 'USD', value: money(total), items: cart.map(gaItem) });
  },
  orderCompleted(order: StorefrontOrder) {
    const cfg = current();
    if (!cfg || cfg.ecommerce_events === '0' || !allowed(cfg)) return;
    if (purchaseAlreadySent(order.id)) return;
    const gtag = ensureGtag();
    const currency = order.currency || cfg.currency || 'USD';
    if (cfg.user_provided_data === '1' && order.email) gtag('set', 'user_data', { email: order.email });
    gtag('event', 'purchase', {
      transaction_id: order.order_number || order.id,
      currency,
      value: money(order.total),
      tax: money(order.tax ?? 0),
      shipping: money(order.shipping ?? 0),
      coupon: order.coupon ?? undefined,
      items: (order.items ?? []).map(gaItem),
    });
    if (cfg.ads_id && cfg.ads_purchase_label) {
      gtag('event', 'conversion', { send_to: `${cfg.ads_id}/${cfg.ads_purchase_label}`, value: money(order.total), currency, transaction_id: order.order_number || order.id });
    }
  },
};

/** Config captured by load(); the other hooks receive no cfg. */
let liveConfig: Record<string, string> | null = null;
function current(): Record<string, string> | null {
  return liveConfig;
}

/** Test seam. */
export function _resetGoogleTagsAdapterForTests(): void {
  liveConfig = null;
}
